import { useIsMobile, useSnackbar, useMapboxRouteAnimation } from 'src/hooks';
import { useState, useEffect, useRef } from 'react';
import { MobileSwipeableDrawer, Slide, Box, Loader, DrawerHeader, IconButton, ArrowBackIcon, 
  CallToActionHero, MapBackdrop, ExplorerPlusIcon } from 'src/components';
import { TripMap, TripDesktopModal, TripsOverview, AddEditTrip, TripDetails, AddEditStep, AnimationControlsContainer } from './index';
import { getTrips, useAuth, TripMapProvider } from 'src/utils';
import MembershipsModal from 'src/pages/memberships/memberships-modal';

import TripTrackingFeatureVideo from 'src/assets/videos/features/trip-tracking-hero.mp4';

export default function TrackingContainer({footerHeight}) {
  const [showMobileDrawer, setShowMobileDrawer] = useState(true);
  const [mobileDrawerState, setMobileDrawerState] = useState("half");
  const [mobileDrawerHeight, setMobileDrawerHeight] = useState(null);
  const [hideMobileDrawerMapButton, setHideMobileDrawerMapButton] = useState(false);
  const [isDesktopViewExpanded, setIsDesktopViewExpanded] = useState(true);
  const [isDesktopWideView, setIsDesktopWideView] = useState(false);
  const [currentView, setCurrentView] = useState("Overview");
  const [inVisualisationView, setInVisualisationView] = useState(false);
  const [displayMembershipsModal, setDisplayMembershipsModal] = useState(false);

  const [selectedTrip, setSelectedTrip] = useState(null);
  const [selectedTripSteps, setSelectedTripSteps] = useState([]);
  const [selectedStepToView, setSelectedStepToView] = useState(null);
  const [selectedStepToEdit, setSelectedStepToEdit] = useState(null);
  const [totalTripKms, setTotalTripKms] = useState(null);
  const [totalTripTime, setTotalTripTime] = useState(null);
  const [tripsOverviewTab, setTripsOverviewTab] = useState(0);
  const [isPolarstepsTrip, setIsPolarstepsTrip] = useState(false);
  const [loadingRoute, setLoadingRoute] = useState(false);

  const [loading, setLoading] = useState(false);
  const [trips, setTrips] = useState([]);

  const mapRef = useRef(null);
  const routeRef = useRef([]);
  const { startAnimation, stopAnimation } = useMapboxRouteAnimation(mapRef.current);

  const isMobile = useIsMobile();
  const { openSnackbar } = useSnackbar();
  const { user } = useAuth();

  const isMembershipsEnabled = import.meta.env.VITE_FEATURE_MEMBERSHIPS === 'true';

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        setLoading(true);
        const res = await getTrips();
        setTrips(res); 
      } catch (err) {
        console.error('Failed to load trips:', err);
        handleHttpFailure('Failed to load trips.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchTrips();
    }
  }, [user]);

  const handleViewChange = (newView, tripData = null, stepData = null, polarsteps = false) => {
    if (tripData) setSelectedTrip(tripData);
    if (stepData) setSelectedStepToEdit(stepData);
    setIsPolarstepsTrip(polarsteps);
    if (newView !== "Overview") setHideMobileDrawerMapButton(true)
    if (newView === "Overview") {
      setIsDesktopWideView(false);
      setHideMobileDrawerMapButton(false);
      setSelectedTripSteps([]);
    }
    else if (newView === "TripDetails") {
      setIsDesktopWideView(true);
    }
    setCurrentView(newView);
  };

  const handleTripAdded = (newTrip) => {
    setTrips(prev => [...prev, newTrip]);
    setSelectedTripSteps([]);
  };

  const handleTripUpdated = (updatedTrip) => {
    setTrips(prev => prev.map(t => t.id === updatedTrip.id ? updatedTrip : t));
  };

  const handleTripDeleted = (tripId) => {
    setTrips((prevTrips) => prevTrips.filter((trip) => trip.id !== tripId));
  };

  const handleStepAdded = () => {
    setSelectedTrip(prev => {
      const updated = { ...prev, stepCount: prev.stepCount + 1 };

      setTrips(trips =>
        trips.map(t => t.id === updated.id ? { ...t, stepCount: updated.stepCount } : t)
      );

      return updated;
    });
  };

  const handleStepDeleted = () => {
    setSelectedTrip(prev => {
      const newCount = Math.max(0, prev.stepCount - 1);
      const updated = { ...prev, stepCount: newCount };

      setTrips(trips =>
        trips.map(t => t.id === updated.id ? { ...t, stepCount: newCount } : t)
      );

      return updated;
    });
  };

  const handleHttpSuccess = (message) => {
      openSnackbar(message, 'success');
  };

  const handleHttpFailure = (message) => {
      openSnackbar(message, 'error');
  };

  const toggleVisualisationView = () => {
      if (isMobile) {
        console.log("set showMobileDrawer to:", !showMobileDrawer)
        setShowMobileDrawer(!showMobileDrawer);
      } else {
        setIsDesktopViewExpanded(!isDesktopViewExpanded);
      }
      console.log("set inVisualisationView to:", !inVisualisationView)
      setInVisualisationView(!inVisualisationView);
  };

  const getPreviewContent = () => {
    switch (currentView) {
      case "Overview":
        if (tripsOverviewTab === 1) { //Stats tab
          return <DrawerHeader title="Track Your Trips" showBack={false} isLoading={loadingRoute} />;
        } else if (tripsOverviewTab === 2) { //Create tab
          return <DrawerHeader title="Visualise Your Trips" showBack={false} isLoading={loadingRoute} />;
        } else {
          return <DrawerHeader title="Track Your Trips" showBack={false} isLoading={loadingRoute} />;
        }
      case "AddTrip":
        return <DrawerHeader title={"New Trip"} onBack={() => handleViewChange('Overview')}/>;
      case "EditTrip":
        return <DrawerHeader title="Edit Trip" onBack={() => handleViewChange('TripDetails')}/>;
      case "TripDetails":
        return <DrawerHeader 
                  title={selectedTrip?.name} 
                  onBack={() => handleViewChange('Overview')}
                  isLoading={loadingRoute}
                  backText={
                    <IconButton 
                      size="small" 
                      disableRipple>
                          <ArrowBackIcon fontSize="small" />
                    </IconButton>
                  }/>;
      case "AddStep":
        return <DrawerHeader title="New Step" onBack={() => handleViewChange('TripDetails')}/>;
      case "EditStep":
        return <DrawerHeader title="Edit Step" onBack={() => handleViewChange('TripDetails')}/>;
      default:
        return <DrawerHeader title="Track Your Trips" showBack={false} isLoading={loadingRoute}/>;
    }
  };
  const views = (setDrawerState) => ({
          "Overview": (
            <TripsOverview 
              trips={trips} 
              handleTripSelect={handleViewChange}
              setDrawerState={setDrawerState}
              setSelectedTripSteps={setSelectedTripSteps}
              totalTripKms={totalTripKms}
              totalTripTime={totalTripTime}
              setTripsOverviewTab={setTripsOverviewTab}
              toggleVisualisationView={toggleVisualisationView}
              mapRef={mapRef} />),
          "AddTrip": (
            <AddEditTrip 
              trip={null} 
              handleViewChange={handleViewChange} 
              onTripAdded={handleTripAdded} 
              handleHttpSuccess={handleHttpSuccess} 
              handleHttpFailure={handleHttpFailure}
              showPolarstepsUrl={isPolarstepsTrip} />),
          "EditTrip": (
            <AddEditTrip 
              trip={selectedTrip} 
              handleViewChange={handleViewChange} 
              onTripUpdated={handleTripUpdated} 
              onTripDeleted={handleTripDeleted} 
              handleHttpSuccess={handleHttpSuccess} 
              handleHttpFailure={handleHttpFailure} />),
          "TripDetails": (
            <Slide direction="left" in={true} mountOnEnter unmountOnExit timeout={500}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <TripDetails 
                  trip={selectedTrip} 
                  steps={selectedTripSteps}
                  setSteps={setSelectedTripSteps}
                  handleViewChange={handleViewChange}
                  handleHttpFailure={handleHttpFailure}
                  transportMode={user?.transportMode}
                  setDrawerState={setDrawerState}
                  totalTripKms={totalTripKms}
                  totalTripTime={totalTripTime}
                  toggleVisualisationView={toggleVisualisationView}
                  setSelectedStep={setSelectedStepToView}
                  mapRef={mapRef} />
              </Box>
            </Slide>),
          "AddStep": (
            <AddEditStep 
              tripId={selectedTrip?.id} 
              tripStartDate={selectedTrip?.startDate}
              step={null} 
              onStepAdded={handleStepAdded} 
              handleViewChange={handleViewChange} 
              handleHttpSuccess={handleHttpSuccess} 
              handleHttpFailure={handleHttpFailure}
              setDrawerState={setDrawerState} />),
          "EditStep": (
            <AddEditStep 
              tripId={selectedTrip?.id} 
              tripStartDate={selectedTrip?.startDate}
              step={selectedStepToEdit} 
              onStepDeleted={handleStepDeleted} 
              handleViewChange={handleViewChange} 
              handleHttpSuccess={handleHttpSuccess} 
              handleHttpFailure={handleHttpFailure} />),
  });

  if (!user) {
    return (
      <CallToActionHero
        title="Track Your Trip!"
        description="Create a stunning digital logbook with photos and memories to share with family and friends – complete with impressive stats like miles conquered and countries explored."
        video={TripTrackingFeatureVideo}
      />
    );
  }

  return (
    <div
      style={{
          position: 'relative',
          height: '100%',
          width: '100vw',
          overflow: 'hidden'
      }}>

        <TripMapProvider>

          <div style={{ 
            position: 'absolute', 
            inset: 0, 
            zIndex: 0,
            padding: inVisualisationView && isMobile ? '70px 40px 150px 40px' : 0}}>

            <TripMap 
              mapRef={mapRef}
              routeRef={routeRef}
              startAnimation={startAnimation}
              stopAnimation={stopAnimation}
              steps={selectedTripSteps} 
              selectedStep={selectedStepToView}
              transportMode={user?.transportMode} 
              setLoadingRoute={setLoadingRoute}
              setTotalTripKms={setTotalTripKms}
              setTotalTripTime={setTotalTripTime}
              mobileDrawerHeight={isMobile ? mobileDrawerHeight : null}
              mobileDrawerState={mobileDrawerState}
              inVisualisationView={inVisualisationView}
              isDesktopViewExpanded={isDesktopViewExpanded} /> 

          </div>

          <MapBackdrop
            open={trips?.length === 0 || selectedTripSteps?.length === 0}
            isMobile={isMobile}
            isDesktopViewExpanded={isDesktopViewExpanded}
            mobileDrawerState={mobileDrawerState}
            contentBgColor="rgba(240, 240, 240, 0.7)"
            message={trips?.length === 0 ? "Create a trip to get started" : "Select a trip to get started"}
          />

          <AnimationControlsContainer 
            mapRef={mapRef}
            routeRef={routeRef}
            startAnimation={startAnimation}
            stopAnimation={stopAnimation}
            toggleVisualisationView={toggleVisualisationView}
            inVisualisationView={inVisualisationView}/>

          {isMobile ? (
            <MobileSwipeableDrawer
                open={showMobileDrawer}
                footerHeight={footerHeight}
                defaultState="half"
                hideMapButton={hideMobileDrawerMapButton}
                previewText={getPreviewContent()}
                onStateChange={setMobileDrawerState}
                onHeightChange={setMobileDrawerHeight}>
                
                {({ updateState  }) => (<>
                    {loading ? 
                      <Box sx={{height: '40%', position: 'relative'}}>
                        <Loader /> 
                      </Box>
                    : <>
                        { views(updateState)[currentView] }</>
                    }
                </>)}

            </MobileSwipeableDrawer>
          ) : 
            <div style={{position: 'absolute', top: 0, zIndex: 10, width: '100%', pointerEvents: 'none'}}>
              
              <TripDesktopModal 
                isExpanded={isDesktopViewExpanded} 
                isDesktopWideView={isDesktopWideView}
                setIsExpanded={setIsDesktopViewExpanded}>

                {loading ? 
                    <Loader /> 
                : <>
                    { views(null)[currentView] }</>
                }

              </TripDesktopModal>

            </div>
          }

        </TripMapProvider>

        {loadingRoute && !isMobile && (
          <MapBackdrop
            open={loadingRoute}
            isDesktopViewExpanded={isDesktopViewExpanded}
            mobileDrawerState={mobileDrawerState}
            showSpinner
          />
        )}

        {user?.membership === "Free" && isMembershipsEnabled && (
          <Box sx={{
              position: 'absolute',
              top: isMobile ? 'calc(15px + var(--safe-area-top))' : (isDesktopViewExpanded ? 35 : 30),
              left: isMobile ? 'auto' : (!isDesktopViewExpanded ? 230 : (isDesktopWideView ? 465 : 395)),
              right: isMobile ? 15 : 'auto',
              transition: 'all 0.4s ease-in-out',
              cursor: 'pointer'
            }}
            onClick={() => setDisplayMembershipsModal(true)}>

            <ExplorerPlusIcon />

          </Box>
        )}

        {displayMembershipsModal && (
            <MembershipsModal
                open={displayMembershipsModal}
                onClose={() => setDisplayMembershipsModal(false)}
            />
        )}
        
    </div>
  );
}
